import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';
import { UDBTF_GUARANTEE_SCAN_COPY_TEXT } from './constants.ts';
import UdbtfGuaranteeScanCopySection from './UdbtfGuaranteeScanCopySection.tsx';
import UdbtfGuaranteeIssueDetailsSection from './UdbtfGuaranteeIssueDetailsSection.tsx';
import { useUdbtfGuaranteeScanCopyPage } from './useUdbtfGuaranteeScanCopyPage.ts';

const UdbtfGuaranteeScanCopyPage: React.FC = () => {
  const {
    application,
    areIssueDetailsDisabled,
    deletingDocumentId,
    effectiveApplicationId,
    handleAddDocument,
    handleCloseAlertModal,
    handleCloseSuccessModal,
    handleCloseUdbtfAccessModal,
    handleDeleteUploadedDocument,
    handleOpenDocument,
    handleSaveIssueGuarantee,
    handleSubmitApprovalLetterByUdbtf,
    hasApplicationFilesLoaded,
    headerProps,
    isApplicationLoading,
    isDocumentActionsDisabled,
    isPreviewLoading,
    isSavingIssueGuarantee,
    isSubmitDisabled,
    isSubmittingApprovalLetterByUdbtf,
    isUdbtfAccessDenied,
    isUdbtfAccessLoading,
    modalAlert,
    modalSuccess,
    previewUrl,
    redirectPath,
    showGuaranteesRequestError,
    uploadingDocumentId,
  } = useUdbtfGuaranteeScanCopyPage();
  const submitStartIcon = isSubmittingApprovalLetterByUdbtf ? <ButtonLoadingIcon /> : undefined;

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isUdbtfAccessLoading) {
    return <FullPageLoader />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={isUdbtfAccessDenied ? handleCloseUdbtfAccessModal : handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      <ApplicationHeader {...headerProps} />

      {isUdbtfAccessDenied ? null : isApplicationLoading || !hasApplicationFilesLoaded ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <UdbtfGuaranteeScanCopySection
              isDeleting={!!deletingDocumentId}
              isDisabled={isDocumentActionsDisabled}
              isPreviewLoading={isPreviewLoading}
              isUploading={!!uploadingDocumentId}
              previewUrl={previewUrl}
              onAddDocument={handleAddDocument}
              onDeleteDocument={handleDeleteUploadedDocument}
              onOpenDocument={handleOpenDocument}
            />
            <UdbtfGuaranteeIssueDetailsSection
              application={application}
              isDisabled={areIssueDetailsDisabled}
              isSaving={isSavingIssueGuarantee}
              onSubmit={handleSaveIssueGuarantee}
            />
            <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
              <Button
                type="button"
                variant="contained"
                disabled={isSubmitDisabled}
                startIcon={submitStartIcon}
                onClick={() => {
                  void handleSubmitApprovalLetterByUdbtf();
                }}
              >
                {UDBTF_GUARANTEE_SCAN_COPY_TEXT.confirmButton}
              </Button>
            </Box>
          </GuaranteesManagerApplicationSidePanelLayout>
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default UdbtfGuaranteeScanCopyPage;
